<script setup lang="ts">
import { Dropdown } from '@bluebrick/saas-ui'
import { uniqueId } from 'lodash-es'
import { storeToRefs } from 'pinia'
import { ref, watch } from 'vue'
import { isSpecialEventTemplate, sortByHumanNameComparator } from '@/common/utils'
import { useEventDetailsStore } from '@/features/eventDetails/useEventDetailsStore'
import { useEventsStore } from '@/features/events/useEventsStore'
import { useTelemetry } from '@/features/telemetry/useTelemetry'
import type { ProductDropdownData, SkuData } from '@/types'

const { store, products } = storeToRefs(useEventDetailsStore())
const { toggleSKUSelection } = useEventsStore()
const { storeSKUs } = storeToRefs(useEventsStore())
const { trackEvent } = useTelemetry()

const toggleSku = (sku: SkuData) => {
	trackEvent('user', isProductChecked(sku.unique_sku_id) ? 'Side panel | Unchecked product.' : 'Side panel | Checked product.', {
		store: store.value?.displayName
	})

	if (store.value) {
		toggleSKUSelection(sku.unique_sku_id)
	}
}

function isProductChecked(skuId: string) {
	return !!products.value.find((product) => product.uniqueSkuId === skuId)
}

const dropdownData = ref<ProductDropdownData>({ items: [] })

function onSelected(data: ProductDropdownData) {
	if (data.items === undefined) {
		dropdownData.value.items?.forEach((sku) => {
			if (!isProductChecked(sku.data.unique_sku_id)) {
				toggleSku(sku.data)
			}
		})
	} else {
		dropdownData.value.items?.forEach((sku) => {
			const productSelected = isProductChecked(sku.data.unique_sku_id)

			if ((productSelected && sku.selected === false) || (!productSelected && sku.selected === true)) {
				toggleSku(sku.data)
			}
		})
	}
}

watch(
	() => storeSKUs.value(store.value?.id),
	() => {
		dropdownData.value = {
			items: storeSKUs
				.value(store.value?.id)
				.filter((sku) => !isSpecialEventTemplate(sku))
				.sort(sortByHumanNameComparator)
				.map((sku) => ({
					id: sku.unique_sku_id,
					name: sku.human_name,
					data: sku,
					selected: isProductChecked(sku.unique_sku_id),
					parent: 'root'
				})),
			hash: uniqueId()
		}
	},
	{ immediate: true }
)

watch(
	products,
	(curr) => {
		storeSKUs.value(store.value?.id).forEach((sku) => {
			const isInProductOptions = curr.find((el) => el.uniqueSkuId === sku.unique_sku_id)
			const isSelected = dropdownData.value.items?.find((el) => el.data.unique_sku_id === sku.unique_sku_id && el.selected)

			if (isInProductOptions && !isSelected) {
				dropdownData.value = {
					items: dropdownData.value.items?.map((el) => {
						if (el.id === sku.unique_sku_id) {
							return { ...el, selected: true }
						}

						return el
					}),
					hash: uniqueId()
				}
			}

			if (!isInProductOptions && isSelected) {
				dropdownData.value = {
					items: dropdownData.value.items?.map((el) => {
						if (el.id === sku.unique_sku_id) {
							return { ...el, selected: false }
						}

						return el
					}),
					hash: uniqueId()
				}
			}
		})
	},
	{ deep: true }
)
</script>

<template>
	<div class="dropdownWrapper">
		<Dropdown dropdown-id="productsSearch" :data="dropdownData" placeholder="Select products" show-counter @selected:data="onSelected" />
	</div>
</template>

<style scoped lang="scss">
.dropdownWrapper {
	:deep(.dropdownItemWrapper) {
		height: 2.5rem;
		padding-right: theme('spacing.04');
	}
}
</style>
